import type { ComponentPropsWithoutRef } from 'react';
import ContentLoader from 'react-content-loader';
import styled from 'styled-components';
import { ColorBrandBlue5, ColorNeutralGrey5 } from 'autogen/design-tokens/tokens';
import type { VideoMediaItem } from 'components/GalleryMediaPreviewer/shared/types';
import Image from 'components/generic/Image';
import useVideoThumbnail from 'components/video/hooks/useVideoThumbnail';
import dynamic from 'helpers/dynamic';
import { MediaCarouselItem } from '../../GalleryThumbnailCarousel';

const StyledImg = styled(Image.WithScaling)`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PlayIcon = styled(dynamic(() => import('icons/Play.svg?react'), { ssr: false }))`
    position: absolute;
    height: 32px;
    width: 32px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

function ThumbnailSkeleton() {
    return (
        <ContentLoader
            speed={2}
            preserveAspectRatio="none"
            width="100%"
            height="100%"
            viewBox="0 0 80 85"
            backgroundColor={ColorNeutralGrey5}
            foregroundColor={ColorBrandBlue5}
        >
            <rect x="0" y="0" rx="0" ry="0" width="80" height="85" />
        </ContentLoader>
    );
}

function Content({ isLoading, data }: { isLoading: boolean; data?: string | null }) {
    if (isLoading || !data) {
        return <ThumbnailSkeleton />;
    }

    return (
        <>
            <PlayIcon />
            <StyledImg alt="" src={data} sizes={94} transformType="WidthScale" />
        </>
    );
}

export const ThumbnailGalleryVideoItem = ({
    videoItem,
    isActive,
    ...restProps
}: {
    videoItem: VideoMediaItem;
    isActive: boolean;
} & ComponentPropsWithoutRef<'button'>) => {
    const { data, isLoading } = videoItem.video.thumbnailUrl
        ? { data: videoItem.video.thumbnailUrl, isLoading: false }
        : useVideoThumbnail(videoItem.video.id, videoItem.video.platform);

    return (
        <MediaCarouselItem $isActive={isActive} {...restProps}>
            <Content isLoading={isLoading} data={data} />
        </MediaCarouselItem>
    );
};

export default ThumbnailGalleryVideoItem;
